import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { DateRangePicker } from "@/components/ui/DateRangePicker";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { normalizeSearchText } from "@/utils/stringUtils";
import { SearchableSelect } from "@/components/ui/SearchableSelect";

const ACTION_LABELS = {
  login_success: "Giriş",
  login_auto_locked: "Hatalı Giriş Nedeniyle Otomatik Kilit",
  credential_setup_completed: "İlk Şifre/Parola Kurulumu Tamamlandı",
  temporary_login_code_issued: "Tek Kullanımlık Giriş Kodu Üretildi",
  forgot_password_sms_sent: "Şifremi Unuttum SMS Kodu Gönderildi",
  password_updated: "Şifre Güncelleme",
  recovery_parola_updated: "Kurtarma Parolası Güncelleme",
  personnel_created: "Personel Ekleme",
  personnel_updated: "Personel Güncelleme",
  personnel_deleted: "Personel Silme",
  personnel_login_blocked: "Giriş Engeli (Açıldı)",
  personnel_login_unblocked: "Giriş Engeli (Kaldırıldı)",
  park_created: "Park Ekleme",
  park_updated: "Park Güncelleme",
  park_deleted: "Park Silme",
  region_created: "Bölge Ekleme",
  region_updated: "Bölge Güncelleme",
  region_deleted: "Bölge Silme",
  leave_request_created: "İzin Talebi Oluşturma",
  leave_request_approved: "İzin Onaylandı",
  leave_request_approved_step: "İzin Aşama Onayı",
  leave_request_rejected: "İzin Reddedildi",
  leave_request_rejected_step: "İzin Bir Üste Sevk Edildi",
  leave_request_escalated: "İzin Üst Makama Sevk",
  leave_request_deleted: "İzin Talebi Silindi",
  delegation_created: "Vekalet Oluşturuldu",
  delegation_returned: "Vekalet İade Edildi",
  data_management_archive_restore: "Arşivden Geri Yükleme",
  data_management_backup_restored: "Yedekten Geri Yükleme",
  data_management_version_restored: "Versiyondan Geri Yükleme",
  data_management_full_backup_restored: "Tam Yedekten Geri Yükleme",
  data_management_archive_by_date: "Tarihe Göre Arşivleme",
  data_management_permanent_delete: "Kalıcı Silme",
  data_management_direct_delete: "Doğrudan Silme",
  data_management_counters_reset: "Sayaç Sıfırlama",
  authz_denied: "Yetkisiz İşlem Denemesi (Reddedildi)",
  bulk_delete: "İşlem Geçmişi Toplu Silme",
};

// F1-E: backend 365 günden kısa saklama süresini reddeder (karar K-9).
const RETENTION_MIN_DAYS = 365;

const ENTITY_LABELS = {
  auth: "Kimlik",
  personnel: "Personel",
  park: "Park",
  region: "Bölge",
  leave_request: "İzin Talebi",
  delegation: "Vekalet",
  data_management: "Veri Yönetimi",
  security: "Güvenlik",
  audit_log: "İşlem Geçmişi",
};

const FIELD_LABELS = {
  name: "Adı Soyadı",
  custom_role_name: "Özel Rol",
  custom_role_description: "Özel Rol Açıklaması",
  phone: "Telefon",
  email: "E-posta",
  status: "Durum",
  current_approver_id: "Güncel Onaylayıcı",
  current_approver_role: "Güncel Onaylayıcı Rolü",
  action: "İşlem",
  comment: "Yorum",
  password_hash: "Şifre",
  login_blocked: "Giriş Engeli",
  credential_setup_completed: "İlk Kurulum Tamamlandı",
  login_failed_attempts: "Hatalı Giriş Sayısı",
  last_failed_login_at: "Son Hatalı Giriş Zamanı",
  self_service: "Kullanıcı İşlemi",
  role: "Rol",
  parent_id: "Üst Yönetici",
  formen_id: "Formen",
  bolge_muhendisi_id: "Bölge Mühendisi",
  mudur_yardimcisi_id: "Müdür Yardımcısı",
  region_id: "Bölge",
  region_ids: "Bölgeler",
  assigned_park_ids: "Sorumlu Parklar",
  neighborhood: "Mahalle",
  address: "Adres",
  latitude: "Enlem",
  longitude: "Boylam",
  boundaries: "Sınırlar",
  coordinates: "Koordinat",
  area: "Toplam Alan",
  grass_area: "Yeşil Alan",
  hard_surface_area: "Sert Zemin Alanı",
  playground_area: "Oyun Alanı",
  light_pole_count: "Aydınlatma Direği Sayısı",
  bench_count: "Bank Sayisi",
  trash_bin_count: "Çöp Kovası Sayısı",
  gazebo_count: "Kamelya Sayisi",
  tree_count: "Ağaç Sayısı",
  flower_count: "Çiçek Sayısı",
  has_running_track: "Yürüyüş/Koşu Pisti",
  running_track_length: "Pist Uzunluğu",
  running_track_width: "Pist Genişliği",
  running_track_type: "Pist Türü",
  has_playground: "Çocuk Oyun Alanı",
  playground_surface_type: "Oyun Alanı Zemini",
  has_sports_equipment: "Spor Aleti Var",
  sports_equipment_area: "Spor Alanı",
  sports_equipment_surface_type: "Spor Alanı Zemini",
  has_wc: "WC",
  has_fountain: "Çeşme",
  fountain_status: "Çeşme Durumu",
  has_parking: "Otopark",
  irrigation_system: "Sulama Sistemi",
  has_basketball_court: "Basketbol Sahası",
  has_football_field: "Futbol Sahası",
  has_volleyball_court: "Voleybol Sahası",
  has_tennis_court: "Tenis Kortu",
  description: "Açıklama",
  assigned_personnel_ids: "Atanan Personeller",
  assigned_personnel_names: "Atanan Personel İsimleri",
  assigned_bolge_muhendisi_id: "Atanan Bölge Mühendisi",
  assigned_formen_ids: "Atanan Formenler",
  assigned_bolge_muhendisi_name: "Atanan Bölge Mühendisi Adı",
  error_code: "Ret Kodu",
  status_code: "HTTP Durumu",
  method: "İstek Türü",
  path: "İstek Adresi",
  route: "Uç",
  detail: "Ret Açıklaması",
  suppressed_since_last: "Bastırılan Tekrar Deneme",
  deleted_count: "Silinen Kayıt",
  protected_skipped: "Korunduğu İçin Silinmeyen",
  retention_days: "Saklama Süresi (gün)",
  purged_old_count: "Silinen Eski Kayıt",
};

const prettifyUnknownField = (key) =>
  String(key || "")
    .replaceAll("_", " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const getFieldLabel = (key) => FIELD_LABELS[key] || prettifyUnknownField(key);
const getActionLabel = (rawAction) => {
  const key = String(rawAction || "").trim();
  if (!key) return "-";
  const normalized = key.toLowerCase().replaceAll(" ", "_");
  return ACTION_LABELS[key] || ACTION_LABELS[normalized] || key;
};

const MODULE_FILTERS = [
  {
    id: "all",
    label: "Tümü",
    matches: () => true,
  },
  {
    id: "auth",
    label: "Giriş / Kimlik",
    matches: (log) => String(log?.entity_type || "").toLowerCase() === "auth",
  },
  {
    id: "personnel",
    label: "Personel",
    matches: (log) => String(log?.entity_type || "").toLowerCase() === "personnel",
  },
  {
    id: "parks",
    label: "Parklar",
    matches: (log) => String(log?.entity_type || "").toLowerCase() === "park",
  },
  {
    id: "regions",
    label: "Bölgeler",
    matches: (log) => String(log?.entity_type || "").toLowerCase() === "region",
  },
  {
    id: "leave",
    label: "İzin Yönetimi",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("leave_") || action.includes("return_request") || entity === "leave_request";
    },
  },
  {
    id: "revisions",
    label: "Revize Yönetimi",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("revision_") || entity === "revision";
    },
  },
  {
    id: "materials",
    label: "Malzeme",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("material_") || entity === "material_request";
    },
  },
  {
    id: "delegations",
    label: "Vekalet",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("delegation_") || action.startsWith("substitution_") || entity === "delegation";
    },
  },
  {
    id: "data-management",
    label: "Veri Yönetimi",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("data_management_") || entity === "data_management";
    },
  },
  {
    id: "overtime",
    label: "Mesai",
    matches: (log) => {
      const action = String(log?.action || "").toLowerCase();
      const entity = String(log?.entity_type || "").toLowerCase();
      return action.startsWith("overtime_") || entity === "overtime";
    },
  },
];

const formatDate = (iso) => {
  if (!iso) return "-";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleString("tr-TR");
};

export default function AuditLogTab({ apiBase, isVisible }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorText, setErrorText] = useState("");
  const [actionQuery, setActionQuery] = useState("");
  const [actorSearch, setActorSearch] = useState("");
  const [targetSearch, setTargetSearch] = useState("");
  const [selectedModule, setSelectedModule] = useState("all");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [retentionDays, setRetentionDays] = useState("365");
  const [savingRetention, setSavingRetention] = useState(false);
  const [cleanupMode, setCleanupMode] = useState("date_range");
  const [cleanupConfirmText, setCleanupConfirmText] = useState("");
  const [cleaningLogs, setCleaningLogs] = useState(false);
  const [infoText, setInfoText] = useState("");
  const activeRetentionDays = Math.max(RETENTION_MIN_DAYS, Math.min(3650, Number(retentionDays) || RETENTION_MIN_DAYS));

  const fetchLogs = async () => {
    setLoading(true);
    setErrorText("");
    setInfoText("");
    try {
      const params = new URLSearchParams();
      params.set("limit", "800");
      if (startDate) params.set("start_date", startDate);
      if (endDate) params.set("end_date", endDate);
      const url = `${apiBase}/api/audit-logs?${params.toString()}`;
      const res = await axios.get(url, { timeout: 30000 });
      const items = Array.isArray(res?.data?.items) ? res.data.items : [];
      setLogs(items);
    } catch (error) {
      const msg = error?.response?.data?.detail || "İşlem geçmişi alınamadı";
      setErrorText(typeof msg === "string" ? msg : "İşlem geçmişi alınamadı");
      setLogs([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isVisible) {
      fetchLogs();
      fetchRetention();
    }
  }, [isVisible]);

  const fetchRetention = async () => {
    try {
      const res = await axios.get(`${apiBase}/api/audit-logs/retention`, { timeout: 15000 });
      const days = String(res?.data?.retention_days || "365");
      setRetentionDays(days);
    } catch (_) {
      // Sessiz geç: tablo çalışmaya devam etsin
    }
  };

  const saveRetention = async () => {
    setSavingRetention(true);
    setErrorText("");
    setInfoText("");
    try {
      const normalized = Math.max(RETENTION_MIN_DAYS, Math.min(3650, Number(retentionDays) || RETENTION_MIN_DAYS));
      await axios.put(`${apiBase}/api/audit-logs/retention`, { retention_days: normalized }, { timeout: 20000 });
      setRetentionDays(String(normalized));
      await fetchLogs();
      setInfoText(`Saklama süresi ${normalized} gün olarak kaydedildi.`);
    } catch (error) {
      const msg = error?.response?.data?.detail || "Saklama süresi kaydedilemedi";
      setErrorText(typeof msg === "string" ? msg : "Saklama süresi kaydedilemedi");
    } finally {
      setSavingRetention(false);
    }
  };

  const cleanAuditLogs = async () => {
    setCleaningLogs(true);
    setErrorText("");
    setInfoText("");
    try {
      const payload = {
        mode: cleanupMode,
        confirm_text: cleanupConfirmText,
      };
      if (cleanupMode === "date_range") {
        payload.start_date = startDate;
        payload.end_date = endDate;
      }
      const res = await axios.delete(`${apiBase}/api/audit-logs`, {
        data: payload,
        timeout: 30000,
      });
      const deleted = Number(res?.data?.deleted_count || 0);
      setInfoText(`Temizleme tamamlandı. Silinen kayıt: ${deleted}`);
      await fetchLogs();
    } catch (error) {
      const msg = error?.response?.data?.detail || "İşlem geçmişi temizlenemedi";
      setErrorText(typeof msg === "string" ? msg : "İşlem geçmişi temizlenemedi");
    } finally {
      setCleaningLogs(false);
    }
  };

  const moduleCounts = useMemo(() => {
    const out = {};
    MODULE_FILTERS.forEach((module) => {
      out[module.id] = logs.filter((log) => module.matches(log)).length;
    });
    return out;
  }, [logs]);

  const selectedModuleFilter = useMemo(
    () => MODULE_FILTERS.find((m) => m.id === selectedModule) || MODULE_FILTERS[0],
    [selectedModule]
  );

  const filteredLogs = useMemo(() => {
    const actionText = normalizeSearchText(actionQuery);
    const actorText = normalizeSearchText(actorSearch);
    const targetText = normalizeSearchText(targetSearch);
    return logs.filter((log) => {
      if (!selectedModuleFilter.matches(log)) return false;
      const actionRaw = normalizeSearchText(log?.action);
      const actionLabel = normalizeSearchText(getActionLabel(log?.action));
      const actorName = normalizeSearchText(log?.actor?.name);
      const targetName = normalizeSearchText(log?.target?.name);
      const actionOk = !actionText || actionRaw.includes(actionText) || actionLabel.includes(actionText);
      const actorOk = !actorText || actorName.includes(actorText);
      const targetOk = !targetText || targetName.includes(targetText);
      return actionOk && actorOk && targetOk;
    });
  }, [logs, actionQuery, actorSearch, targetSearch, selectedModuleFilter]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>İşlem Geçmişi</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex flex-wrap gap-2">
          {MODULE_FILTERS.map((module) => {
            const active = selectedModule === module.id;
            const count = moduleCounts[module.id] || 0;
            return (
              <Button
                key={module.id}
                type="button"
                variant={active ? "default" : "outline"}
                size="sm"
                onClick={() => setSelectedModule(module.id)}
              >
                {module.label} ({count})
              </Button>
            );
          })}
        </div>

        <div className="border rounded-lg p-3 bg-gray-50">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
            <div>
              <Label>İşlem Geçmişi Saklama Süresi</Label>
              <div className="mt-1 mb-2 inline-flex items-center rounded-full bg-blue-100 text-blue-800 px-3 py-1 text-xs font-semibold">
                Şu an aktif süre: {activeRetentionDays} gün
              </div>
              <SearchableSelect
                className="mt-1 w-full"
                value={retentionDays}
                onChange={(v) => setRetentionDays(v)}
                showAllOption={false}
                placeholder="Seçin"
                searchPlaceholder="Ara..."
                options={[
                  { id: "365", name: "1 yıl (asgari)" },
                  { id: "730", name: "2 yıl" },
                  { id: "1095", name: "3 yıl" },
                ]}
              />
            </div>
            <div>
              <Label>Özel Gün</Label>
              <Input
                type="number"
                min={RETENTION_MIN_DAYS}
                max={3650}
                value={retentionDays}
                onChange={(e) => setRetentionDays(e.target.value)}
              />
            </div>
            <div className="md:col-span-2">
              <Button type="button" onClick={saveRetention} disabled={savingRetention}>
                {savingRetention ? "Kaydediliyor..." : "Saklama Süresini Kaydet"}
              </Button>
            </div>
          </div>
        </div>

        <div className="border rounded-lg p-3 bg-red-50">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-3 items-end">
            <div>
              <Label>Temizleme Yöntemi</Label>
              <SearchableSelect
                className="mt-1 w-full"
                value={cleanupMode}
                onChange={(v) => setCleanupMode(v)}
                showAllOption={false}
                placeholder="Seçin"
                searchPlaceholder="Ara..."
                options={[
                  { id: "date_range", name: "Seçili tarih aralığını temizle" },
                  { id: "older_than_retention", name: "Saklama süresinden eskiyi temizle" },
                  { id: "all", name: "Tümünü temizle (yalnız Süper Admin)" },
                ]}
              />
            </div>
            <div className="md:col-span-2">
              <Label>Güvenlik Onayı (tümünü silmek için EMINIM yaz)</Label>
              <Input
                value={cleanupConfirmText}
                onChange={(e) => setCleanupConfirmText(e.target.value)}
                placeholder="tümünü silmek için: EMINIM"
              />
            </div>
            <div className="md:col-span-2">
              <Button
                type="button"
                variant="destructive"
                onClick={cleanAuditLogs}
                disabled={cleaningLogs}
              >
                {cleaningLogs ? "Temizleniyor..." : "İşlem Geçmişini Temizle"}
              </Button>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div className="md:col-span-2">
            <DateRangePicker
              label="Tarih Aralığı"
              startDate={startDate}
              endDate={endDate}
              onChange={({ start, end }) => {
                setStartDate(start);
                setEndDate(end);
              }}
              placeholder="Tarih aralığı"
            />
          </div>
          <div>
            <Label>İşlem</Label>
            <Input
              value={actionQuery}
              onChange={(e) => setActionQuery(e.target.value)}
              placeholder="işlem adına göre ara"
            />
          </div>
          <div>
            <Label>İşlemi Yapan</Label>
            <Input
              value={actorSearch}
              onChange={(e) => setActorSearch(e.target.value)}
              placeholder="ad ile ara"
            />
          </div>
          <div>
            <Label>Etkilenen Kayıt</Label>
            <Input
              value={targetSearch}
              onChange={(e) => setTargetSearch(e.target.value)}
              placeholder="ad ile ara"
            />
          </div>
          <div className="flex items-end">
            <Button onClick={fetchLogs} disabled={loading} className="w-full">
              {loading ? "Yükleniyor..." : "Listeyi Yenile"}
            </Button>
          </div>
          <div className="flex items-end">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setStartDate("");
                setEndDate("");
              }}
            >
              Tarihleri Temizle
            </Button>
          </div>
        </div>

        {errorText && (
          <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded p-2">
            {errorText}
          </div>
        )}
        {infoText && (
          <div className="text-sm text-green-700 bg-green-50 border border-green-200 rounded p-2">
            {infoText}
          </div>
        )}

        <div className="border rounded-md max-h-[65vh] overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0">
              <tr>
                <th className="text-left p-2 border-b">Tarih</th>
                <th className="text-left p-2 border-b">Yapan</th>
                <th className="text-left p-2 border-b">İşlem</th>
                <th className="text-left p-2 border-b">Tür</th>
                <th className="text-left p-2 border-b">Etkilenen</th>
                <th className="text-left p-2 border-b">Değişiklik</th>
              </tr>
            </thead>
            <tbody>
              {filteredLogs.length === 0 ? (
                <tr>
                  <td className="p-3 text-gray-500" colSpan={6}>
                    Bu bölümde kayıt bulunamadı.
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => {
                  const fields = Object.keys(log?.changes?.fields || {});
                  const fieldLabels = fields.map(getFieldLabel);
                  return (
                    <tr key={log.id} className="odd:bg-white even:bg-gray-50">
                      <td className="p-2 align-top">{formatDate(log.created_at)}</td>
                      <td className="p-2 align-top">
                        {log?.actor?.name || "-"}
                        <div className="text-xs text-gray-500">{log?.actor?.role || "-"}</div>
                      </td>
                      <td className="p-2 align-top">{getActionLabel(log.action)}</td>
                      <td className="p-2 align-top">{ENTITY_LABELS[log.entity_type] || log.entity_type || "-"}</td>
                      <td className="p-2 align-top">{log?.target?.name || log?.target?.id || "-"}</td>
                      <td className="p-2 align-top">
                        {fieldLabels.length > 0 ? fieldLabels.slice(0, 6).join(", ") : "-"}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
